import { useEffect, useMemo, useState } from 'react';

import { useBillsQuery } from '../../api/getBills';
import { usePoliciesQuery } from '../../api/getPolicies';
import { useWelcomeQuery } from '../../api/getWelcomeDetails';
import { usePreferencesQuery } from '../../api/preferences/getPreferences';
import PaperlessPreferencesDialog from '../../pages/Overview/dialogs/PaperlessPreferencesDialog';
import {
  RecentlyLinkedPolicyContextType,
  useRecentlyLinkedContext,
} from '../../store/recentlyLinkedPolicy';
import { getWithExpiry } from '../../utils/helpers';
import TermsAndConditionsDialog from '../TermsAndConditionsDialog/TermsAndConditionsDialog';
import useUpdatePreferences, {
  areAnyEnrolledOrPending,
  isEnrolled,
  isPending,
} from '../../hooks/useUpdatePreferences';

const PaperlessDialogs = () => {
  const declinedPaperless = !!getWithExpiry('declinedPaperless');
  const randomInteger = useMemo(() => Number(new Date().getSeconds()) % 3, []);
  const [tAndCModalShown, setTAndCModalShown] = useState<boolean>(false);
  const [showPaperlessPreferencesDialog, setShowPaperlessPreferencesDialog] =
    useState<boolean>(false);
  const [openTAndCDialog, setOpenTAndCDialog] = useState(false);
  const [paperlessDialogShown, setPaperlessDialogShown] = useState(false);
  const { recentlyLinkedPolicyNumber } =
    useRecentlyLinkedContext() as RecentlyLinkedPolicyContextType;
  const { updatePreferences, isLoading: isUpdatePreferencesLoading } =
    useUpdatePreferences();

  const { data: welcomeData } = useWelcomeQuery();
  const { data: policiesData } = usePoliciesQuery(!!welcomeData);
  const { data: billsData } = useBillsQuery(!!welcomeData);
  const authToken = welcomeData?.authToken ?? '';

  const activePolicies = policiesData?.policies.filter(
    (policy) => policy.policyStatus.toLowerCase() === 'active'
  );

  // Only use policies that are available in both the /bills (CDH) and /policies (GPD) responses
  const verifiedPolicies = activePolicies?.filter((policy) =>
    billsData?.payments.find(
      (payment) => payment.policyNumber === policy.policyNumber
    )
  );

  const { data: preferencesData } = usePreferencesQuery(
    authToken,
    verifiedPolicies?.map((policy) => ({
      policyNumber: policy.policyNumber,
      policySourceSystem: policy.dataSource,
    }))
  );

  const eligiblePolicies = preferencesData?.selections.filter((selection) =>
    billsData?.payments.find(
      (payment) => payment.policyNumber === selection.policyNumber
    )
  );

  const somePoliciesEnrolled = eligiblePolicies?.some((selection) =>
    isEnrolled(selection)
  );
  const somePoliciesUnenrolled = eligiblePolicies?.some(
    (selection) => !areAnyEnrolledOrPending(selection)
  );

  const showEnrollAllPoliciesVariation =
    somePoliciesEnrolled && somePoliciesUnenrolled;

  const contentSelection = showEnrollAllPoliciesVariation ? 3 : randomInteger;

  const pendingSelections = preferencesData?.selections.filter((selection) =>
    isPending(selection)
  );

  const isBillingAndTextIneligiblePolicies = billsData?.payments
    .filter(
      (payment) => payment.policyStatus !== 'Active' || payment.mortgageeBilled
    )
    .map((payment) => payment.policyNumber);

  const email = pendingSelections?.length ? pendingSelections[0].email : '';

  const recentlyLinkedWithoutPaperlessPending =
    preferencesData?.selections?.find(
      (preference) =>
        recentlyLinkedPolicyNumber === preference?.policyNumber &&
        preference?.policyDocuments === ''
    );

  useEffect(() => {
    if (pendingSelections?.length && !recentlyLinkedWithoutPaperlessPending) {
      setOpenTAndCDialog(true);
    } else if (recentlyLinkedWithoutPaperlessPending) {
      setOpenTAndCDialog(false);
    }
  }, [pendingSelections?.length, recentlyLinkedWithoutPaperlessPending]);

  const triggerPaperlessModalDialog = !!billsData && !!preferencesData;

  useEffect(() => {
    const allPoliciesEnrolled = eligiblePolicies?.every((selection) =>
      isEnrolled(selection)
    );

    if (!paperlessDialogShown) {
      setShowPaperlessPreferencesDialog(
        !declinedPaperless &&
          !allPoliciesEnrolled &&
          triggerPaperlessModalDialog &&
          !tAndCModalShown &&
          !openTAndCDialog
      );
    }
  }, [
    triggerPaperlessModalDialog,
    declinedPaperless,
    tAndCModalShown,
    openTAndCDialog,
    paperlessDialogShown,
  ]);

  const handleAcceptTermsAndConditions = async () => {
    await updatePreferences({
      type: 'paperless',
      enrolled: true,
      isBillingAndTextIneligiblePolicies,
      onSuccess: () => {
        setOpenTAndCDialog(false);
        setTAndCModalShown(true);
      },
      onError: () => {
        setOpenTAndCDialog(false);
      },
    });
  };

  return (
    <>
      <TermsAndConditionsDialog
        email={email as string}
        open={openTAndCDialog}
        closeDialog={() => {
          setOpenTAndCDialog(false);
          setTAndCModalShown(true);
        }}
        acceptTermsAndConditions={handleAcceptTermsAndConditions}
        showAlert={!!recentlyLinkedPolicyNumber}
        isLoading={isUpdatePreferencesLoading}
        showPaperlessText
        showCancel
      />
      <PaperlessPreferencesDialog
        open={showPaperlessPreferencesDialog}
        setShowPaperlessPreferencesDialog={setShowPaperlessPreferencesDialog}
        contentSelection={contentSelection}
        setPaperlessDialogShown={setPaperlessDialogShown}
      />
    </>
  );
};

export default PaperlessDialogs;
